import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  ConsumedResource, 
  UpgradableNumber,
} from "@dndModels";
import { 
  REFRESH_RULES,
  SPELL_LEVEL_OPTIONS,
} from "@dndModels/Constants";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import { isSpellLevel } from '@dndModels/Types';

interface Props {
  obj: ConsumedResource;
  onChange: (changed: ConsumedResource) => void; 
  disabled: boolean;
  no_amount?: boolean;
  attached_table?: AttachedTable;
}

export default function ConsumedResourceInput({
  obj,
  disabled,
  no_amount = false,
  onChange,
  attached_table
}: Props) {
  const [ping, setPing] = useState(false);

  const renderRecharge = () => {
    if (obj.resource_consumed === "Recharge") {
      const recharge_on_options: string[] = [];
      for (let i = 1; i <= obj.recharge_die; ++i) {
        recharge_on_options.push(`${i}`);
      }
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox  
              name="Recharge Die"
              options={["4","6","8","10","12","20","100"]}
              value={ `${obj.recharge_die}` }
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj.clone();
                new_obj.recharge_die = +changed as 4 | 6 | 8 | 10 | 12 | 20 | 100;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox  
              name="Recharge On"
              options={recharge_on_options}
              values={obj.recharge_on.map(o => `${o}`)}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj.clone();
                new_obj.recharge_on = changed.map(o => +o);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  }

  const renderSpecial = () => {
    // console.log(obj.resource_consumed);
    // console.log(REFRESH_RULES);
    // debugger;
    if (obj.resource_consumed && obj.resource_consumed === "Special") {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <UpgradableNumberBox 
              name="Special Resource Amount"
              value={obj.special_resource_amount} 
              disabled={disabled}
              quick_options={["STR","DEX","CON","INT","WIS","CHA","Prof"]}
              attached_table={attached_table}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj.clone();
                new_obj.special_resource_amount = value;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Refresh Rule"
              options={REFRESH_RULES}
              value={ obj.special_resource_refresh_rule } 
              disabled={disabled}
              // log_it
              onChange={(value: string) => {
                const new_obj = obj.clone();
                new_obj.special_resource_refresh_rule = value;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}>
        <SelectResourceBox 
          name="Resource Consumed"
          extra_options={["Special","None","Slot","Pact","Charge","Recharge","Infusion","Legendary Action"]}
          value={ obj.resource_consumed ? obj.resource_consumed : "None" }
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj.clone();
            new_obj.resource_consumed = changed;
            if (changed === "Special") {
              if (new_obj.special_resource_refresh_rule === "") {
                new_obj.special_resource_refresh_rule = "Long Rest";
              }
              if (new_obj.special_resource_amount.isBlank()) {
                new_obj.special_resource_amount.base = 1;
              }
            }
            if (changed !== "None") {
              if (new_obj.amount_consumed === 0) {
                new_obj.amount_consumed = 1;
              }
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        /> 
      </Grid>
      { !no_amount && obj.resource_consumed && obj.resource_consumed !== "None" && obj.resource_consumed !== "Recharge" &&
        <>
        { obj.resource_consumed === "Slot" ?
          <>
            <Grid size={6}>
              <StringBox 
                name="Amount Consumed"
                value={obj.amount_consumed} 
                type="number"
                disabled={disabled}
                onBlur={(value: number) => {
                  const new_obj = obj.clone();
                  new_obj.amount_consumed = +value;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SelectStringBox 
                name="Minimum Slot Level"
                value={`${obj.slot_level}`} 
                options={SPELL_LEVEL_OPTIONS}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj.clone();
                  const slot_level = +changed;
                  if (isSpellLevel(slot_level)) {
                    new_obj.slot_level = slot_level;
                  }
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
          </>
        :
          <Grid size={12}>
            <StringBox 
              name="Amount Consumed"
              value={obj.amount_consumed} 
              type="number"
              disabled={disabled}
              onBlur={(value: number) => {
                const new_obj = obj.clone();
                new_obj.amount_consumed = +value;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        }
        </>
      }
      { renderSpecial() }
      { renderRecharge() }
    </Grid>
  );
}
